import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';

const EMPTY_VENDOR = { name: '', type: '', industry: '', payment_terms_days: 30, tax_id: '', website: '', status: 'active' };
const EMPTY_CONTACT = { name: '', email: '', phone: '', designation: '', is_primary: false };
const TYPE_OPTIONS = ['Supplier', 'Contractor', 'Service Provider', 'Consultant', 'Other'];
const STATUS_OPTIONS = ['active', 'inactive', 'blacklisted'];

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_VENDOR });
  const [saving, setSaving] = useState(false);

  // Detail view
  const [detailVendor, setDetailVendor] = useState(null);
  const [detailTab, setDetailTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contactModal, setContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ ...EMPTY_CONTACT });

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchVendors = useCallback(async () => {
    try {
      let url = '/api/vendors?limit=200';
      if (filterType) url += `&type=${filterType}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setVendors(data.vendors || []);
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, [filterType, filterStatus]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  async function fetchDetail(vendor) {
    setDetailVendor(vendor);
    setDetailTab('contacts');
    try {
      const res = await apiFetch(`/api/vendors/${vendor.id}/overview`);
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
        setBankDetails(data.bankDetails || []);
        setPurchaseOrders(data.purchaseOrders || []);
        setInvoices(data.invoices || []);
      }
    } catch (e) { /* handled */ }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/vendors/${editing.id}` : '/api/vendors';
      const res = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) {
        setModalOpen(false);
        setEditing(null);
        setForm({ ...EMPTY_VENDOR });
        fetchVendors();
      }
    } catch (err) { /* handled */ }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!confirm('Delete this vendor?')) return;
    await apiFetch(`/api/vendors/${id}`, { method: 'DELETE' });
    fetchVendors();
  }

  async function handleSaveContact() {
    try {
      const res = await apiFetch(`/api/vendors/${detailVendor.id}/contacts`, {
        method: 'POST', body: JSON.stringify(contactForm)
      });
      if (res.ok) {
        setContactModal(false);
        setContactForm({ ...EMPTY_CONTACT });
        fetchDetail(detailVendor);
      }
    } catch (e) { /* handled */ }
  }

  function openEdit(vendor) {
    setEditing(vendor);
    setForm({ name: vendor.name || '', type: vendor.type || '', industry: vendor.industry || '', payment_terms_days: vendor.payment_terms_days || 30, tax_id: vendor.tax_id || '', website: vendor.website || '', status: vendor.status || 'active' });
    setModalOpen(true);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_VENDOR });
    setModalOpen(true);
  }

  // Detail view
  if (detailVendor) {
    return (
      <div>
        <button className="btn btn-secondary" onClick={() => setDetailVendor(null)} style={{ marginBottom: 16 }}>Back to Vendors</button>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h3>{detailVendor.name}</h3>
            <span className={`badge ${detailVendor.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{detailVendor.status}</span>
          </div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div><strong>Type:</strong> {detailVendor.type || '-'}</div>
            <div><strong>Industry:</strong> {detailVendor.industry || '-'}</div>
            <div><strong>Payment Terms:</strong> {detailVendor.payment_terms_days} days</div>
            <div><strong>Tax ID:</strong> {detailVendor.tax_id || '-'}</div>
            <div><strong>Website:</strong> {detailVendor.website || '-'}</div>
          </div>
        </div>

        <div className="tabs" style={{ marginBottom: 16 }}>
          {['contacts', 'bank', 'orders', 'invoices'].map(tab => (
            <button key={tab} className={`tab-btn ${detailTab === tab ? 'active' : ''}`} onClick={() => setDetailTab(tab)}>
              {tab === 'contacts' ? 'Contacts' : tab === 'bank' ? 'Bank Details' : tab === 'orders' ? 'Purchase Orders' : 'Invoices'}
            </button>
          ))}
        </div>

        {detailTab === 'contacts' && (
          <div className="card">
            <div className="card-header">
              <h4>Contacts</h4>
              <button className="btn btn-primary btn-sm" onClick={() => setContactModal(true)}>Add Contact</button>
            </div>
            <table className="data-table">
              <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Designation</th><th>Primary</th></tr></thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td><td>{c.email || '-'}</td><td>{c.phone || '-'}</td>
                    <td>{c.designation || '-'}</td><td>{c.is_primary ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
                {contacts.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No contacts</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {detailTab === 'bank' && (
          <div className="card">
            <div className="card-header"><h4>Bank Details</h4></div>
            <table className="data-table">
              <thead><tr><th>Bank</th><th>Account #</th><th>IFSC</th><th>SWIFT</th><th>Currency</th></tr></thead>
              <tbody>
                {bankDetails.map(b => (
                  <tr key={b.id}><td>{b.bank_name}</td><td>{b.account_number}</td><td>{b.ifsc_code || '-'}</td><td>{b.swift_code || '-'}</td><td>{b.currency}</td></tr>
                ))}
                {bankDetails.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No bank details</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {detailTab === 'orders' && (
          <div className="card">
            <div className="card-header"><h4>Purchase Orders</h4></div>
            <table className="data-table">
              <thead><tr><th>PO #</th><th>Date</th><th>Amount</th><th>Status</th></tr></thead>
              <tbody>
                {purchaseOrders.map(po => (
                  <tr key={po.id}><td>{po.po_number}</td><td>{po.order_date || '-'}</td><td>{parseFloat(po.total_amount || 0).toLocaleString()}</td><td><span className="badge">{po.status}</span></td></tr>
                ))}
                {purchaseOrders.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No purchase orders</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {detailTab === 'invoices' && (
          <div className="card">
            <div className="card-header"><h4>Invoices</h4></div>
            <table className="data-table">
              <thead><tr><th>Invoice #</th><th>Date</th><th>Total</th><th>Status</th></tr></thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.id}><td>{inv.invoice_number}</td><td>{inv.invoice_date || '-'}</td><td>{parseFloat(inv.total || 0).toLocaleString()}</td><td><span className="badge">{inv.status}</span></td></tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No invoices</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Contact Modal */}
        {contactModal && (
          <div className="modal-overlay" onClick={() => setContactModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
              <div className="modal-header"><h3>Add Contact</h3><button className="modal-close" onClick={() => setContactModal(false)}>X</button></div>
              <div className="modal-body">
                <div className="form-group"><label>Name *</label><input className="form-control" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} /></div>
                <div className="form-group"><label>Email</label><input className="form-control" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} /></div>
                <div className="form-group"><label>Phone</label><input className="form-control" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} /></div>
                <div className="form-group"><label>Designation</label><input className="form-control" value={contactForm.designation} onChange={e => setContactForm({ ...contactForm, designation: e.target.value })} /></div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setContactModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveContact} disabled={!contactForm.name}>Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-control" value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 160 }}>
            <option value="">All Types</option>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="form-control" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 140 }}>
            <option value="">All Status</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ New Vendor</button>
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Type</th><th>Industry</th><th>Payment Terms</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : vendors.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8' }}>No vendors found</td></tr>
            ) : vendors.map(v => (
              <tr key={v.id}>
                <td><a href="#" onClick={e => { e.preventDefault(); fetchDetail(v); }} style={{ color: '#6366f1', fontWeight: 500 }}>{v.name}</a></td>
                <td>{v.type || '-'}</td>
                <td>{v.industry || '-'}</td>
                <td>{v.payment_terms_days} days</td>
                <td><span className={`badge ${v.status === 'active' ? 'badge-green' : v.status === 'blacklisted' ? 'badge-red' : 'badge-gray'}`}>{v.status}</span></td>
                <td>
                  <button className="btn btn-sm btn-secondary" onClick={() => openEdit(v)} style={{ marginRight: 4 }}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(v.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header"><h3>{editing ? 'Edit Vendor' : 'New Vendor'}</h3><button className="modal-close" onClick={() => setModalOpen(false)}>X</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Name *</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Type</label>
                  <select className="form-control" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    <option value="">Select</option>
                    {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Industry</label><input className="form-control" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} /></div>
                <div className="form-group"><label>Payment Terms (days)</label><input type="number" className="form-control" value={form.payment_terms_days} onChange={e => setForm({ ...form, payment_terms_days: parseInt(e.target.value) || 30 })} /></div>
                <div className="form-group"><label>Tax ID</label><input className="form-control" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} /></div>
                <div className="form-group"><label>Website</label><input className="form-control" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></div>
                <div className="form-group"><label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
